import { useMemo, useState } from "react";

import dayjs from "dayjs";
import { Plus } from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  useCreateManualOverride,
  useExchangeRates,
  useUpdateExchangeRate,
} from "~/hooks/exchangeRates";
import type {
  ExchangeRate,
  ManualOverrideFormData,
} from "~/types/exchangeRates";

import { ExchangeRateDialog } from "./components/ExchangeRateDialog";
import { ExchangeRateTable } from "./components/ExchangeRateTable";
import { ExchangeRateWidget } from "./components/ExchangeRateWidget";

export default function ExchangeRatesPage() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRate, setEditingRate] = useState<ExchangeRate | undefined>(
    undefined
  );
  const [dateFrom, setDateFrom] = useState(
    dayjs().subtract(30, "day").format("YYYY-MM-DD")
  );
  const [dateTo, setDateTo] = useState(dayjs().format("YYYY-MM-DD"));

  const filters = useMemo(
    () => ({
      dateFrom,
      dateTo,
    }),
    [dateFrom, dateTo]
  );

  const { data: rates = [], isLoading } = useExchangeRates(filters);
  const createMutation = useCreateManualOverride();
  const updateMutation = useUpdateExchangeRate();

  const handleCreate = () => {
    setEditingRate(undefined);
    setDialogOpen(true);
  };

  const handleEdit = (rate: ExchangeRate) => {
    setEditingRate(rate);
    setDialogOpen(true);
  };

  const handleSubmit = (data: ManualOverrideFormData) => {
    if (editingRate) {
      updateMutation.mutate(
        { id: editingRate.id, data },
        {
          onSuccess: () => {
            setDialogOpen(false);
            setEditingRate(undefined);
          },
        }
      );
    } else {
      createMutation.mutate(data, {
        onSuccess: () => {
          setDialogOpen(false);
        },
      });
    }
  };

  return (
    <PageContainer>
      <PageHeader
        title="Tipos de Cambio"
        description="Gestión de tipos de cambio USD/CLP"
        actions={
          <Button className="h-9" onClick={handleCreate}>
            <Plus className="mr-2 h-4 w-4" />
            Agregar
          </Button>
        }
      />

      {/* Widget with current rate */}
      <ExchangeRateWidget />

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
            <div className="grid gap-1.5">
              <Label htmlFor="dateFrom" className="text-xs">
                Desde
              </Label>
              <Input
                id="dateFrom"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="h-9 w-full sm:w-40"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="dateTo" className="text-xs">
                Hasta
              </Label>
              <Input
                id="dateTo"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="h-9 w-full sm:w-40"
              />
            </div>
            <Button
              variant="ghost"
              className="h-9 w-full sm:w-auto"
              onClick={() => {
                setDateFrom(dayjs().subtract(30, "day").format("YYYY-MM-DD"));
                setDateTo(dayjs().format("YYYY-MM-DD"));
              }}
            >
              Limpiar filtros
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium">
              Historial de Tipos de Cambio
            </CardTitle>
            <span className="text-sm text-muted-foreground">
              {rates.length} registros
            </span>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <ExchangeRateTable
            rates={rates}
            isLoading={isLoading}
            onEdit={handleEdit}
          />
        </CardContent>
      </Card>

      <ExchangeRateDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        rate={editingRate}
        onSubmit={handleSubmit}
        isLoading={createMutation.isPending || updateMutation.isPending}
      />
    </PageContainer>
  );
}
